import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import { Table } from "@models/table";
import { CustomTableItem } from "@models/custom-table-item";

export const customTablesApi = createApi({
  reducerPath: "customTablesApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["CustomTables", "CustomTablesOptions"],
  endpoints: (b) => ({
    
    getCustomTables: b.query<Table[], void>({
      query: () => ({
        url: "custom-tables",
        params: { sort: "index" },
      }),
      providesTags: ["CustomTables"],
      keepUnusedDataFor: Infinity,
    }),

    getCustomTableOptions: b.query<CustomTableItem[], number>({
      query: (tableId) => ({
        url: "custom-tables-items",
        params: {
          sort: "index",
          "filter[custom_table_id]": tableId,
        },
      }),
      providesTags: (_res, _err, tableId) => [{ type: "CustomTablesOptions", id: tableId }],
      keepUnusedDataFor: Infinity,
    }),

    editCustomTable: b.mutation<Table, Table>({
      query: (customTableToUpdate) => ({
        url: `custom-tables/${customTableToUpdate.id}`,
        method: "PATCH",
        data: customTableToUpdate,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          customTablesApi.util.updateQueryData("getCustomTables", undefined, (draft) => {
            const row = draft.find((t) => t.id === arg.id);
            if (row) Object.assign(row, arg);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, arg) => [
        { type: "CustomTables" as const, id: arg.id },
        { type: "CustomTables" as const, id: "LIST" as const },
      ],
    }),

    editCustomTableOptionById: b.mutation<CustomTableItem, CustomTableItem>({
      query: (option) => ({
        url: `custom-tables-items/${option.id}`,
        method: "PATCH",
        data: option,
      }),
      async onQueryStarted(option, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          customTablesApi.util.updateQueryData("getCustomTableOptions", option.custom_table_id, (draft) => {
            const row = draft.find((r) => r.id === option.id);
            if (row) Object.assign(row, option);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, { custom_table_id }) => [
        { type: "CustomTablesOptions", id: custom_table_id },
      ],
    }),

    addCustomTable: b.mutation<Table, number>({
      query: (tableIndex) => ({
        url: "custom-tables",
        method: "POST",
        data: {
          index: tableIndex,
          name: "",
        },
      }),
      async onQueryStarted(tableIndex, { dispatch, queryFulfilled }) {
        const temp: Table = { id: 99999, business_id: 99999, index: tableIndex, name: "" };
        const patch = dispatch(
          customTablesApi.util.updateQueryData("getCustomTables", undefined, (draft) => {
            draft.push(temp);
          }),
        );
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            customTablesApi.util.updateQueryData("getCustomTables", undefined, (draft) => {
              const i = draft.findIndex((x) => x.id === temp.id);
              if (i >= 0) draft[i] = created;
            }),
          );
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, tableIndex) => [{ type: "CustomTablesOptions", id: tableIndex }],
    }),

    addCustomTableOption: b.mutation<CustomTableItem, { customTableId: number; optionIndex: number }>({
      query: ({ customTableId, optionIndex }) => ({
        url: "custom-tables-items",
        method: "POST",
        data: {
          custom_table_id: customTableId,
          index: optionIndex,
          name: "",
        },
      }),
      async onQueryStarted({ customTableId, optionIndex }, { dispatch, queryFulfilled }) {
        const temp: CustomTableItem = {
          id: 99999,
          custom_table_id: customTableId,
          business_id: 99999,
          index: optionIndex,
          name: "",
        };
        const patch = dispatch(
          customTablesApi.util.updateQueryData("getCustomTableOptions", customTableId, (draft) => {
            draft.push(temp);
          }),
        );
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            customTablesApi.util.updateQueryData("getCustomTableOptions", customTableId, (draft) => {
              const i = draft.findIndex((x) => x.id === temp.id);
              if (i >= 0) draft[i] = created;
            }),
          );
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, { customTableId }) => [{ type: "CustomTablesOptions", id: customTableId }],
    }),
  }),
});

export const {
  useGetCustomTablesQuery,
  useGetCustomTableOptionsQuery,
  useEditCustomTableMutation,
  useEditCustomTableOptionByIdMutation,
  useAddCustomTableMutation,
  useAddCustomTableOptionMutation,
} = customTablesApi;
